/**
 * FlowPathsPanel
 * For Media Devoted | Created By Ciro Autiero
 *
 * Left column: Active/Inactive paths (no inner Card — lives inside shared bordered region).
 */

import { FC, useCallback, useMemo } from 'react';

import { cn } from '@/lib/utils';
import { useFlowOverlaySort } from '@/features/flows/detail/overlay-sort';
import { usePreviewMode } from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import { FlowOverlayMetricLabels } from '@/features/flows/detail/components/common/FlowOverlayMetricLabels';

import { FlowPathsSectionTitle } from './FlowPathsPanel.parts';
import { FlowPathsPathRow } from './FlowPathsPathRow';

import type { FlowPathListItem } from './FlowPathsPanel.parts';

export type { FlowPathListItem };

type Metrics = Record<string, number>;

interface FlowPathsPanelProps {
  paths: FlowPathListItem[];
  selectedPathId: string;
  expandedPathId: string | null;
  onSelectPath: (pathId: string) => void;
  onToggleExpand: (pathId: string) => void;
  /** Omitted on a read-only flow (Tres Commas): the rows render no switch. */
  onToggleActive?: (pathId: string, active: boolean) => void;
  pathMetricsById?: Record<string, Metrics>;
  metricsRangeLabel?: string;
  isMutating?: boolean;
}

export const FlowPathsPanel: FC<FlowPathsPanelProps> = ({
  paths,
  selectedPathId,
  expandedPathId,
  onSelectPath,
  onToggleExpand,
  onToggleActive,
  pathMetricsById,
  metricsRangeLabel,
  isMutating = false,
}) => {
  const overlaySort = useFlowOverlaySort();
  const previewMode = usePreviewMode();
  const { activePaths, inactivePaths } = useMemo(() => {
    const active = paths.filter((p) => p.active);
    const inactive = paths.filter((p) => !p.active);
    if (!overlaySort) return { activePaths: active, inactivePaths: inactive };
    return {
      activePaths: overlaySort.sortItems(active, 'paths'),
      inactivePaths: overlaySort.sortItems(inactive, 'paths'),
    };
  }, [overlaySort, paths]);

  const handleRowClick = useCallback(
    (pathId: string) => {
      onSelectPath(pathId);
    },
    [onSelectPath],
  );

  const renderRow = (path: FlowPathListItem) => (
    <FlowPathsPathRow
      key={path.id}
      path={path}
      isSelected={path.id === selectedPathId}
      isExpanded={expandedPathId === path.id}
      profit={pathMetricsById?.[path.id]?.profit}
      pathMetrics={pathMetricsById?.[path.id]}
      metricsRangeLabel={metricsRangeLabel}
      isMutating={isMutating}
      onSelect={() => handleRowClick(path.id)}
      onToggleExpand={() => onToggleExpand(path.id)}
      onToggleActive={onToggleActive && ((active) => onToggleActive(path.id, active))}
    />
  );

  const listChrome =
    'divide-y divide-border/40 rounded-md border border-border/50 bg-background/30';

  return (
    <div className="col-span-12 flex min-h-0 flex-col gap-2 py-1.5 pl-2 pr-1 lg:col-span-4 lg:pl-3 lg:pr-1.5">
      <h3 className="text-base font-semibold leading-none tracking-tight">Paths</h3>
      <div className="min-h-0 flex-1 space-y-2">
        {paths.length === 0 ? (
          <p className="text-sm text-muted-foreground">No paths found for this flow.</p>
        ) : (
          <div className="max-h-[min(calc(100vh-15rem),52rem)] space-y-2 overflow-y-auto pr-1">
            <div>
              <FlowPathsSectionTitle title="Active" count={activePaths.length} countKind="paths" />
              {previewMode ? <FlowOverlayMetricLabels variant="paths" /> : null}
              <div className={cn(listChrome)}>{activePaths.map(renderRow)}</div>
            </div>

            <div>
              <FlowPathsSectionTitle title="Inactive" count={inactivePaths.length} countKind="paths" />
              <div className={cn(listChrome)}>{inactivePaths.map(renderRow)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FlowPathsPanel;
